import type { EntregaFormState } from './EntregaFormState.ts';

export type OfflineMapMarker = {
  lat: number;
  lng: number;
  label: string;
  color: string;
};

export const ENTREGA_MAP_COLORS = {
  punto: '#6750A4',
  gps: '#7D5260',
  grid: '#CAC4D0',
  bg: '#F3EDF7',
} as const;

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

const ENTREGA_MAP_VIEW_SPAN_DEG = 0.024;

export function offlineMapMarkerPositions(
  markers: OfflineMapMarker[],
  width: number,
  height: number,
  center?: { lat: number; lng: number },
): Array<OfflineMapMarker & { x: number; y: number }> {
  if (markers.length === 0) return [];
  let south: number;
  let west: number;
  let latSpan: number;
  let lngSpan: number;
  if (center) {
    latSpan = ENTREGA_MAP_VIEW_SPAN_DEG;
    lngSpan = ENTREGA_MAP_VIEW_SPAN_DEG;
    south = center.lat - latSpan / 2;
    west = center.lng - lngSpan / 2;
  } else {
    const lats = markers.map((m) => m.lat);
    const lngs = markers.map((m) => m.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const padLat = Math.max((maxLat - minLat) * 0.15, 0.002);
    const padLng = Math.max((maxLng - minLng) * 0.15, 0.002);
    latSpan = Math.max(maxLat - minLat + padLat * 2, 0.004);
    lngSpan = Math.max(maxLng - minLng + padLng * 2, 0.004);
    south = minLat - padLat;
    west = minLng - padLng;
  }
  return markers.map((m) => ({
    ...m,
    x: ((m.lng - west) / lngSpan) * (width - 24) + 12,
    y: height - 12 - ((m.lat - south) / latSpan) * (height - 24),
  }));
}

export function entregaMapMarkersFromForm(state: EntregaFormState): OfflineMapMarker[] {
  const markers: OfflineMapMarker[] = [];
  if (state.puntoLat != null && state.puntoLng != null) {
    markers.push({ lat: state.puntoLat, lng: state.puntoLng, label: 'Punto', color: ENTREGA_MAP_COLORS.punto });
  }
  if (state.gpsLat != null && state.gpsLng != null) {
    markers.push({ lat: state.gpsLat, lng: state.gpsLng, label: 'GPS', color: ENTREGA_MAP_COLORS.gps });
  }
  return markers;
}

/** Viewport follows GPS only; punto never pans or zooms the map. */
export function entregaMapMarkersInView(state: EntregaFormState): {
  markers: OfflineMapMarker[];
  gpsFarKm: number | null;
} {
  const all = entregaMapMarkersFromForm(state);
  const punto = all.find((m) => m.label === 'Punto');
  const gps = all.find((m) => m.label === 'GPS');
  let gpsFarKm: number | null = null;
  if (punto && gps) gpsFarKm = haversineKm(punto, gps);
  const markers = gps ? [gps] : [];
  return { markers, gpsFarKm };
}

/** OSM embed when online; null → SVG fallback. */
export function entregaMapEmbedUrl(state: EntregaFormState): string | null {
  const { markers: viewport } = entregaMapMarkersInView(state);
  if (viewport.length === 0) return null;
  const center = viewport[0]!;
  const pad = ENTREGA_MAP_VIEW_SPAN_DEG / 2;
  const minLat = center.lat - pad;
  const maxLat = center.lat + pad;
  const minLng = center.lng - pad;
  const maxLng = center.lng + pad;
  const bbox = `${minLng},${minLat},${maxLng},${maxLat}`;
  const markerQuery = viewport.map((m) => `marker=${encodeURIComponent(`${m.lat},${m.lng}`)}`).join('&');
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&${markerQuery}`;
}

export function prepareEntregaMapView(state: EntregaFormState, width: number, height: number) {
  const all = entregaMapMarkersFromForm(state);
  const { markers: viewport, gpsFarKm } = entregaMapMarkersInView(state);
  const center = viewport[0];
  const positioned = center ? offlineMapMarkerPositions(all, width, height, center) : [];
  const punto = all.find((m) => m.label === 'Punto');
  const gps = all.find((m) => m.label === 'GPS');
  const showLink =
    punto != null &&
    gps != null &&
    gpsFarKm != null &&
    gpsFarKm <= 5 &&
    positioned.length === 2;
  return { positioned, gpsFarKm, showLink, hasCoords: gps != null };
}

export function formatCoordLabel(lat: number | null, lng: number | null): string {
  if (lat == null || lng == null) return '—';
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}
